/**
 * Pure helpers for the wizard's Step 4 (D1.5.5) config intake. Mirrors
 * the persisted shape that `packages/config/src/tenant-config.ts` parses
 * at commit time.
 *
 * The form is a thin layer on top of the canonical schema. The platform
 * wizard uses a single open/close window per day (sufficient for the
 * pet-services SMBs in scope); multi-window days (e.g. split lunch
 * closures) land with the intake portal in D2. Business meta is full
 * (legal entity, EIN optional, address, phone) because compliance
 * requires it regardless of which path an admin takes.
 *
 * Draft values are stored as raw strings/numbers; Zod parsing happens
 * at the commit boundary (D1.6.1), not here.
 */

export const DEFAULT_TIMEZONE = 'America/Denver';
export const DEFAULT_CURRENCY = 'USD';
export const DEFAULT_LOCALE = 'en-US';

// ISO 8601 day-of-week: 1=Mon … 7=Sun. Matches business-hours schema keys.
export const DAY_KEYS = ['1', '2', '3', '4', '5', '6', '7'] as const;
export type DayKey = (typeof DAY_KEYS)[number];

export const DAY_LABELS: Record<DayKey, string> = {
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday',
  '7': 'Sunday',
};

export const ENTITY_TYPES = ['LLC', 'Corp', 'S-Corp', 'SoleProp', 'Partnership', 'Other'] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];

// ─── Hours validation ────────────────────────────────────────────────────────

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const EIN_PATTERN = /^\d{2}-\d{7}$/;

export type HoursValidationError =
  | 'open_missing'
  | 'close_missing'
  | 'open_malformed'
  | 'close_malformed'
  | 'close_not_after_open';

export type HoursValidation =
  | { ok: true }
  | { ok: false; reason: HoursValidationError };

export function validateBusinessHoursWindow(open: string, close: string): HoursValidation {
  if (open.length === 0) return { ok: false, reason: 'open_missing' };
  if (close.length === 0) return { ok: false, reason: 'close_missing' };
  if (!TIME_PATTERN.test(open)) return { ok: false, reason: 'open_malformed' };
  if (!TIME_PATTERN.test(close)) return { ok: false, reason: 'close_malformed' };
  if (close <= open) return { ok: false, reason: 'close_not_after_open' };
  return { ok: true };
}

export function describeHoursError(reason: HoursValidationError): string {
  switch (reason) {
    case 'open_missing':
      return 'Opening time is required.';
    case 'close_missing':
      return 'Closing time is required.';
    case 'open_malformed':
      return 'Opening time must be HH:MM (24-hour).';
    case 'close_malformed':
      return 'Closing time must be HH:MM (24-hour).';
    case 'close_not_after_open':
      return 'Closing time must be after opening.';
  }
}

// ─── Draft shape ─────────────────────────────────────────────────────────────

export type BusinessHoursRowDraft = {
  enabled: boolean;
  open: string;
  close: string;
};

export type BusinessHoursDraft = Partial<Record<DayKey, BusinessHoursRowDraft>>;

export type ServiceEntryDraft = {
  slug: string;
  displayName: string;
  priceCents: number;
  durationMinutes: number;
  active: boolean;
};

export type BusinessAddressDraft = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
};

export type BusinessMetaDraft = {
  legalEntityName?: string;
  entityType?: EntityType;
  einTaxId?: string;
  primaryPhone?: string;
  primaryAddress?: BusinessAddressDraft;
};

export type TenantConfigDraft = {
  timezone?: string;
  currency?: string;
  locale?: string;
  businessHours?: BusinessHoursDraft;
  serviceCatalog?: ServiceEntryDraft[];
  businessMeta?: BusinessMetaDraft;
};

function readObject(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function readBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function readDayRow(value: unknown): BusinessHoursRowDraft | undefined {
  const obj = readObject(value);
  if (!obj) return undefined;
  return {
    enabled: readBoolean(obj.enabled, true),
    open: readString(obj.open) ?? '',
    close: readString(obj.close) ?? '',
  };
}

function readBusinessHoursDraft(value: unknown): BusinessHoursDraft | undefined {
  const obj = readObject(value);
  if (!obj) return undefined;
  const draft: BusinessHoursDraft = {};
  for (const day of DAY_KEYS) {
    const row = readDayRow(obj[day]);
    if (row) draft[day] = row;
  }
  return draft;
}

function readServiceEntry(value: unknown): ServiceEntryDraft | undefined {
  const obj = readObject(value);
  if (!obj) return undefined;
  const slug = readString(obj.slug);
  const displayName = readString(obj.displayName);
  if (!slug || !displayName) return undefined;
  return {
    slug,
    displayName,
    priceCents: readNumber(obj.priceCents) ?? 0,
    durationMinutes: readNumber(obj.durationMinutes) ?? 0,
    active: readBoolean(obj.active, true),
  };
}

function readServiceCatalog(value: unknown): ServiceEntryDraft[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const entries: ServiceEntryDraft[] = [];
  for (const raw of value) {
    const entry = readServiceEntry(raw);
    if (entry) entries.push(entry);
  }
  return entries;
}

function readAddress(value: unknown): BusinessAddressDraft | undefined {
  const obj = readObject(value);
  if (!obj) return undefined;
  return {
    line1: readString(obj.line1),
    line2: readString(obj.line2),
    city: readString(obj.city),
    state: readString(obj.state),
    postalCode: readString(obj.postalCode),
    country: readString(obj.country),
  };
}

function readEntityType(value: unknown): EntityType | undefined {
  if (typeof value !== 'string') return undefined;
  return (ENTITY_TYPES as readonly string[]).includes(value) ? (value as EntityType) : undefined;
}

function readBusinessMeta(value: unknown): BusinessMetaDraft | undefined {
  const obj = readObject(value);
  if (!obj) return undefined;
  return {
    legalEntityName: readString(obj.legalEntityName),
    entityType: readEntityType(obj.entityType),
    einTaxId: readString(obj.einTaxId),
    primaryPhone: readString(obj.primaryPhone),
    primaryAddress: readAddress(obj.primaryAddress),
  };
}

export function readTenantConfigDraft(payload: Record<string, unknown>): TenantConfigDraft {
  const raw = payload.config;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const r = raw as Record<string, unknown>;
  return {
    timezone: readString(r.timezone),
    currency: readString(r.currency),
    locale: readString(r.locale),
    businessHours: readBusinessHoursDraft(r.businessHours),
    serviceCatalog: readServiceCatalog(r.serviceCatalog),
    businessMeta: readBusinessMeta(r.businessMeta),
  };
}

// ─── Immutable setters ───────────────────────────────────────────────────────

export function setTimezone(current: TenantConfigDraft, value: string): TenantConfigDraft {
  return { ...current, timezone: value };
}

export function setCurrency(current: TenantConfigDraft, value: string): TenantConfigDraft {
  return { ...current, currency: value };
}

export function setLocale(current: TenantConfigDraft, value: string): TenantConfigDraft {
  return { ...current, locale: value };
}

export function setDayEnabled(
  current: TenantConfigDraft,
  day: DayKey,
  enabled: boolean,
): TenantConfigDraft {
  const prevRow = current.businessHours?.[day];
  const nextRow: BusinessHoursRowDraft = {
    enabled,
    open: prevRow?.open ?? '',
    close: prevRow?.close ?? '',
  };
  return {
    ...current,
    businessHours: { ...(current.businessHours ?? {}), [day]: nextRow },
  };
}

export function setDayWindow(
  current: TenantConfigDraft,
  day: DayKey,
  field: 'open' | 'close',
  value: string,
): TenantConfigDraft {
  const prevRow = current.businessHours?.[day];
  const nextRow: BusinessHoursRowDraft = {
    enabled: prevRow?.enabled ?? true,
    open: prevRow?.open ?? '',
    close: prevRow?.close ?? '',
    [field]: value,
  };
  return {
    ...current,
    businessHours: { ...(current.businessHours ?? {}), [day]: nextRow },
  };
}

export function setServiceActive(
  current: TenantConfigDraft,
  slug: string,
  active: boolean,
): TenantConfigDraft {
  const catalog = current.serviceCatalog ?? [];
  return {
    ...current,
    serviceCatalog: catalog.map((s) => (s.slug === slug ? { ...s, active } : s)),
  };
}

export function setServicePriceCents(
  current: TenantConfigDraft,
  slug: string,
  priceCents: number,
): TenantConfigDraft {
  const catalog = current.serviceCatalog ?? [];
  return {
    ...current,
    serviceCatalog: catalog.map((s) => (s.slug === slug ? { ...s, priceCents } : s)),
  };
}

export function setServiceDurationMinutes(
  current: TenantConfigDraft,
  slug: string,
  durationMinutes: number,
): TenantConfigDraft {
  const catalog = current.serviceCatalog ?? [];
  return {
    ...current,
    serviceCatalog: catalog.map((s) =>
      s.slug === slug ? { ...s, durationMinutes } : s,
    ),
  };
}

export function setBusinessMetaField<K extends keyof BusinessMetaDraft>(
  current: TenantConfigDraft,
  key: K,
  value: BusinessMetaDraft[K],
): TenantConfigDraft {
  return {
    ...current,
    businessMeta: { ...(current.businessMeta ?? {}), [key]: value },
  };
}

export function setAddressField(
  current: TenantConfigDraft,
  key: keyof BusinessAddressDraft,
  value: string,
): TenantConfigDraft {
  const meta = current.businessMeta ?? {};
  return {
    ...current,
    businessMeta: {
      ...meta,
      primaryAddress: { ...(meta.primaryAddress ?? {}), [key]: value },
    },
  };
}

// ─── Seeding from vertical defaults ──────────────────────────────────────────

type VerticalDefaultsLike = {
  serviceCatalog: ReadonlyArray<{
    slug: string;
    displayName: string;
    priceCents: number;
    durationMinutes: number;
    active: boolean;
  }>;
  businessHours: {
    schedule: Partial<
      Record<
        DayKey,
        ReadonlyArray<{ open: string; close: string }>
      >
    >;
  };
};

/**
 * Seed the draft from a vertical's canonical defaults — services, hours.
 * Overlays only when the draft field is absent; admin edits are never
 * overwritten.
 */
export function seedFromVerticalDefaults(
  current: TenantConfigDraft,
  defaults: VerticalDefaultsLike,
): TenantConfigDraft {
  const next: TenantConfigDraft = { ...current };
  if (!next.serviceCatalog) {
    next.serviceCatalog = defaults.serviceCatalog.map((s) => ({
      slug: s.slug,
      displayName: s.displayName,
      priceCents: s.priceCents,
      durationMinutes: s.durationMinutes,
      active: s.active,
    }));
  }
  if (!next.businessHours) {
    const hours: BusinessHoursDraft = {};
    for (const day of DAY_KEYS) {
      const windows = defaults.businessHours.schedule[day];
      const firstWindow = windows?.[0];
      if (firstWindow) {
        hours[day] = { enabled: true, open: firstWindow.open, close: firstWindow.close };
      } else {
        hours[day] = { enabled: false, open: '', close: '' };
      }
    }
    next.businessHours = hours;
  }
  if (!next.timezone) next.timezone = DEFAULT_TIMEZONE;
  if (!next.currency) next.currency = DEFAULT_CURRENCY;
  if (!next.locale) next.locale = DEFAULT_LOCALE;
  return next;
}

// ─── EIN / currency / dollar helpers ─────────────────────────────────────────

export function isValidEinOrEmpty(raw: string): boolean {
  if (raw.length === 0) return true;
  return EIN_PATTERN.test(raw);
}

/**
 * Inline phone validator for the wizard. Primary phone is REQUIRED on
 * commit (the server enforces it; previously it only surfaced on the
 * Review step which forced a round-trip). This validator gives Step 4
 * the inline-error UX so the wizard surfaces the requirement at the
 * field, not 3 steps later.
 *
 * Format: lenient — accepts E.164, US-format, common separators. Counts
 * digits and requires 7-15 (E.164 range). Rejects empty (required).
 */
export function isValidPhone(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return false;
  const digits = trimmed.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

export function describePhoneError(raw: string): string {
  if (raw.trim().length === 0) return 'Primary phone is required.';
  return 'Phone must contain 7–15 digits (e.g. +1 303 555 0100).';
}

export function centsToDollarsInput(cents: number): string {
  if (!Number.isFinite(cents) || cents < 0) return '';
  return (cents / 100).toFixed(2);
}

export function dollarsInputToCents(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return 0;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}
